import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { OrderType } from './orders-types';
import { usePermissions } from '../../hooks/usePermissions';
import { ordersActionPermissions } from './orders-datasource';
import { PickupOrderModal, MultiOrderModal } from './orders-action-modals';
import { commitReceiverBank } from '../../../store/slices/receiver-bank-slice';
import {
  cancelOrder,
  unCancelOrder,
  deleteOrder,
  placeOrder,
  acceptCustomerChanges,
  createMiscBillingFromOrder,
  updateIsReceiverBank,
} from '../../../services/requests/orders/orders-requests';

/**
 * Legacy Helium Orders action bar (HeliumOrders.aspx lines 512–522). Renders the
 * order lifecycle buttons with the same state-driven visibility as the legacy
 * `PopulateOrderInfo` orchestration (aspx.cs ~335–384):
 *
 *   - New/unplaced order      → Save, Save & Close, Place Order, Delete, Back
 *   - Placed order            → Save, Save & Close, Cancel Order, Back (+ deferred)
 *   - Closure lockdown (billed, non-admin) → Save only, Back
 *   - Closed without lockdown → Save, Save & Close, Back (+ permitted actions)
 *   - Canceled                → UnCancel Order, Back
 *
 * A "Completed" status (shipped/fulfilled but not billed) is treated as a normal
 * placed order — legacy never hides buttons for it (its `isCompleted` flag is
 * hard-coded false).
 *
 * Save/Save & Close/Place run the shared form submit (validation + create/update)
 * exactly like `SubmitButton`. Cancel/UnCancel/Delete/Place call the dedicated
 * Orders endpoints.
 *
 * Deferred buttons (Accept Changes, Generate Misc. Bill, Generate Pickup Order,
 * Multi-Order) are rendered disabled with a tooltip — they depend on
 * infrastructure not yet ported (CAS acknowledgment email, cross-domain Billing
 * header creation, Dewar manifest copy, and the bulk-order modal + distance
 * web service, respectively).
 */
export const OrderActionBar: React.FC<any> = (props) => {
  const {
    entity,
    watch,
    getValues,
    handleSubmit,
    onSubmit,
    confirm,
    isAdmin,
    entityLoading,
  } = props;

  const navigate = useNavigate();
  const [actionAccess] = usePermissions(ordersActionPermissions);
  const dispatch = useDispatch();
  const pendingReceiverBank = useSelector((state: any) => state.receiverBank?.pending ?? {});
  const [busy, setBusy] = useState(false);
  const [showPickup, setShowPickup] = useState(false);
  const [showMulti, setShowMulti] = useState(false);

  const id = entity?.id;
  const orderNumber = entity?.orderNumber;
  const isPlaced = !!orderNumber && Number(orderNumber) > 0;
  const isCanceled = !!entity?.isCanceled;
  const inLockdown = !!entity?.inOrderClosureLockdown;
  const orderTypeId = Number(watch?.('orderTypeId') ?? entity?.orderTypeId);
  const casOrderState = Number(entity?.casOrderState ?? 0);

  // State machine mirroring the legacy PopulateOrderInfo visibility branches.
  type Mode = 'canceled' | 'new' | 'lockdown' | 'placed';
  let mode: Mode;
  if (isCanceled) mode = 'canceled';
  else if (!isPlaced) mode = 'new';
  else if (inLockdown && !isAdmin) mode = 'lockdown';
  else mode = 'placed';
  // NOTE: a "Completed" status (shipped/fulfilled but NOT billed) is NOT a
  // button-hiding state. Legacy only hides the action buttons via isCanceled,
  // or closure lockdown — its
  // `isCompleted` flag is hard-coded false and never fires. So Completed orders
  // fall through to the normal `placed` button set (Save / Save & Close /
  // Cancel / Misc. Bill / Pickup / Multi-Order), matching legacy.

  const disabled = busy || entityLoading;

  // ---- Handlers -------------------------------------------------------------

  const runSubmit = async () => {
    // Mirrors SubmitButton: validation + create/update via the data source, then
    // flush the buffered Receiver Bank checkbox toggles (legacy UpdateContainersOnSite).
    let submitted = false;
    await handleSubmit(async (formData: any) => {
      await onSubmit(formData);
      const entries = Object.entries(pendingReceiverBank);
      if (entries.length) {
        await Promise.all(entries.map(([id, val]) => updateIsReceiverBank(Number(id), !!val)));
        dispatch(commitReceiverBank());
      }
      submitted = true;
    })();
    return submitted;
  };

  const doSave = async () => {
    setBusy(true);
    try {
      await runSubmit();
    } finally {
      setBusy(false);
    }
  };

  const doSaveAndClose = async () => {
    setBusy(true);
    try {
      if (await runSubmit()) navigate('/orders#My%20Recently%20Changed%20Orders');
    } finally {
      setBusy(false);
    }
  };

  const doPlace = async () => {
    setBusy(true);
    try {
      // Legacy Place Order first saves the form (UpdateOrder) then PlaceOrder.
      if (!await runSubmit()) return;
      await placeOrder(id);
      // Reload to surface the newly-assigned Order Number and refreshed state.
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    // Legacy requires the Order Notes field (tbOtherNotes) to be populated and
    // uses it as the cancellation reason.
    const reason = (getValues?.('otherNotes') ?? '').toString().trim();
    if (!reason) {
      await confirm('Please enter Order Notes describing the reason before canceling.', true);
      return;
    }
    const proceed = await confirm('Are you sure you want to cancel this order?');
    if (!proceed) return;
    setBusy(true);
    try {
      await cancelOrder({ orderId: id, reason });
      navigate('/orders');
    } finally {
      setBusy(false);
    }
  };

  const doUnCancel = async () => {
    const proceed = await confirm('Are you sure you want to un-cancel this order?');
    if (!proceed) return;
    setBusy(true);
    try {
      await unCancelOrder({ orderId: id });
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    const proceed = await confirm('Are you sure you want to delete this order?');
    if (!proceed) return;
    setBusy(true);
    try {
      await deleteOrder({ orderId: id, notes: (getValues?.('otherNotes') ?? '').toString() });
      navigate('/orders');
    } finally {
      setBusy(false);
    }
  };

  const doAcceptChanges = async () => {
    // Legacy btnAcceptCustChanges_Click — save the form, then accept CAS changes
    // (state→Placed + acknowledgment email) and return to the list.
    setBusy(true);
    try {
      if (!await runSubmit()) return;
      await acceptCustomerChanges(id);
      navigate('/orders');
    } finally {
      setBusy(false);
    }
  };

  const doMiscBill = async () => {
    // Legacy btnMiscBilling_Click — create a Misc. bill from this order, then
    // open the billing edit page for the new header.
    const billingWindow = window.open('', '_blank');
    setBusy(true);
    try {
      const header = await createMiscBillingFromOrder(id);
      const billingId = header?.billingId;
      if (billingId && billingWindow) billingWindow.location.href = `/billing/edit/${billingId}`;
      else billingWindow?.close();
    } catch (error) {
      billingWindow?.close();
      throw error;
    } finally {
      setBusy(false);
    }
  };

  // ---- Rendering helpers ----------------------------------------------------

  const spinner = busy ? <Spinner animation="border" size="sm" className="ms-1" /> : null;

  const actionButton = (
    key: string,
    label: string,
    variant: string,
    onClick: () => void,
    enabled = true,
  ) => (
    <Button
      key={key}
      type="button"
      variant={variant}
      size="sm"
      className="me-2 mb-1"
      disabled={disabled || !enabled}
      onClick={onClick}
    >
      {label}
      {spinner}
    </Button>
  );

  const buttons: React.ReactNode[] = [];

  if (mode === 'new') {
    buttons.push(actionButton('save', 'Save Order', 'primary', doSave));
    buttons.push(actionButton('saveClose', 'Save & Close Order', 'primary', doSaveAndClose));
    if (actionAccess) buttons.push(actionButton('place', 'Place Order', 'success', doPlace));
    if (actionAccess) buttons.push(actionButton('delete', 'Delete', 'danger', doDelete));
  } else if (mode === 'placed') {
    buttons.push(actionButton('save', 'Save Order', 'primary', doSave));
    buttons.push(actionButton('saveClose', 'Save & Close Order', 'primary', doSaveAndClose));
    if (actionAccess) buttons.push(actionButton('cancel', 'Cancel Order', 'danger', doCancel));
    if (actionAccess && orderTypeId === OrderType.Gas && casOrderState === 4)
      buttons.push(actionButton('accept', 'Accept Changes & Save Order', 'success', doAcceptChanges));
  } else if (mode === 'lockdown') {
    // Billed & locked (non-admin): only a notes-restricted Save is allowed
    // (backend enforces the restriction).
    buttons.push(actionButton('save', 'Save Order', 'primary', doSave));
  } else if (mode === 'canceled') {
    if (actionAccess) buttons.push(actionButton('uncancel', 'UnCancel Order', 'success', doUnCancel));
  }

  // Legacy enables Generate Misc. Bill for every placed order and never hides
  // it again in the canceled, completed, closed, or closure-lockdown branches.
  if (actionAccess && isPlaced)
    buttons.push(actionButton('misc', 'Generate Misc. Bill', 'outline-secondary', doMiscBill));

  // Legacy leaves Generate Pickup Order available for every placed Dewar order,
  // including canceled, completed, closed, and closure-lockdown states.
  if (actionAccess && isPlaced && orderTypeId === OrderType.Dewar)
    buttons.push(actionButton('pickup', 'Generate Pickup Order', 'outline-secondary', () => setShowPickup(true)));

  // Legacy btnBulkOrder stays visible for placed Gas/Liquid orders in every branch, incl. closed/lockdown.
  if (actionAccess && isPlaced && (orderTypeId === OrderType.Gas || orderTypeId === OrderType.Liquid))
    buttons.push(actionButton('bulk', 'Multi-Order', 'outline-secondary', () => setShowMulti(true)));

  buttons.push(actionButton('back', 'Back', 'outline-secondary', () => navigate('/orders')));

  return (
    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: 0 }}>
      {buttons}
      <PickupOrderModal
        show={showPickup}
        onHide={() => setShowPickup(false)}
        parentOrderId={id}
        orderTypeId={orderTypeId}
        deliveryTypeId={watch?.('deliveryTypeId')}
      />
      <MultiOrderModal
        show={showMulti}
        onHide={() => setShowMulti(false)}
        getValues={getValues}
      />
    </div>
  );
};
